import classNames from 'classnames';
import {
  MutableRefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import './Collapse.scss';

interface Props {
  triggerRenderer: (isOpen: boolean, onTriggerClick: () => void) => JSX.Element;
  contentRenderer: (
    ref: MutableRefObject<HTMLDivElement | null>,
    onTriggerClick: () => void
  ) => JSX.Element;
  className?: string;
  externalControl?: {
    isOpened: boolean;
    onTriggerClick?: () => void;
  };
  initialOpened?: boolean;
}

export default function Collapse({
  triggerRenderer,
  contentRenderer,
  className = '',
  externalControl,
  initialOpened = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [isOpened, setIsOpened] = useState(
    externalControl?.isOpened || initialOpened
  );
  const [maxHeight, setMaxHeight] = useState(0);

  const onTriggerClick = externalControl?.onTriggerClick
    ? externalControl.onTriggerClick
    : () => {
        setIsOpened(!isOpened);
      };

  const isExpanded = externalControl ? externalControl.isOpened : isOpened;

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      setTimeout(() => {
        el.classList.add('Collapse--transition');
      }, 0);
    }
  }, []);

  useLayoutEffect(() => {
    if (ref.current) {
      const el = ref.current;
      const onResize = () => {
        setMaxHeight(isExpanded ? el.offsetHeight + 50 : 0);
      };
      setMaxHeight(isExpanded ? el.offsetHeight + 50 : 0);
      if (isExpanded) {
        window.addEventListener('resize', onResize);
      }

      return () => {
        window.removeEventListener('resize', onResize);
      };
    }
  }, [isExpanded]);

  return (
    <div className={className} ref={containerRef}>
      <div>{triggerRenderer(isExpanded, onTriggerClick)}</div>
      <div
        className={classNames('Collapse__content', {
          'Collapse__content--opened': isExpanded,
        })}
        style={{
          maxHeight,
        }}
      >
        {contentRenderer(ref, onTriggerClick)}
      </div>
    </div>
  );
}
